import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from './useDebounce';

interface UseGoogleSuggestionsOptions {
	delay?: number;
	client?: string;
	minQueryLength?: number;
	maxSuggestions?: number;
}

interface UseGoogleSuggestionsResult {
	suggestions: string[];
	isLoading: boolean;
	error: string | null;
	retry: () => void;
}

export function useGoogleSuggestions(
	query: string,
	options: UseGoogleSuggestionsOptions = {},
): UseGoogleSuggestionsResult {
	const {
		delay = 300,
		client = 'youtube',
		minQueryLength = 2,
		maxSuggestions = 10,
	} = options;

	const [state, setState] = useState<{
		suggestions: string[];
		isLoading: boolean;
		error: string | null;
	}>({
		suggestions: [],
		isLoading: false,
		error: null,
	});

	const scriptRef = useRef<HTMLScriptElement | null>(null);
	const isMountedRef = useRef(true);
	const retryCountRef = useRef(0);
	const maxRetries = 3;

	const debouncedQuery = useDebounce(query, delay);

	const cleanup = useCallback(() => {
		if (scriptRef.current?.parentNode) {
			scriptRef.current.parentNode.removeChild(scriptRef.current);
			scriptRef.current = null;
		}
	}, []);

	const handleError = useCallback(
		(error: string) => {
			if (isMountedRef.current) {
				setState((prev) => ({
					...prev,
					error,
					isLoading: false,
				}));
			}
			cleanup();
		},
		[cleanup],
	);

	const fetchSuggestions = useCallback(() => {
		if (!debouncedQuery || debouncedQuery.length < minQueryLength) {
			setState((prev) => ({
				...prev,
				suggestions: [],
				isLoading: false,
				error: null,
			}));
			return;
		}

		setState((prev) => ({ ...prev, isLoading: true, error: null }));

		const callbackName = `googleSuggestCallback_${Date.now()}`;
		cleanup();

		const script = document.createElement('script');
		scriptRef.current = script;

		const handleSuccess = (data: [string, string[]]) => {
			if (!isMountedRef.current) return;

			const [, suggestions] = data;

			setState((prev) => ({
				...prev,
				suggestions: suggestions
					.map((s) => s?.[0])
					.filter((s) => s !== undefined)
					.slice(0, maxSuggestions),
				isLoading: false,
				error: null,
			}));
			retryCountRef.current = 0;
			cleanup();
		};

		const handleScriptError = () => {
			if (retryCountRef.current < maxRetries) {
				retryCountRef.current += 1;
				setTimeout(fetchSuggestions, 1000);
			} else {
				handleError('Failed to fetch suggestions after multiple attempts');
			}
		};

		(window as any)[callbackName] = handleSuccess;
		script.onerror = handleScriptError;

		const encodedQuery = encodeURIComponent(debouncedQuery);
		script.src = `https://suggestqueries.google.com/complete/search?client=${client}&q=${encodedQuery}&callback=${callbackName}`;

		document.body.appendChild(script);
	}, [
		debouncedQuery,
		client,
		minQueryLength,
		maxSuggestions,
		cleanup,
		handleError,
	]);

	useEffect(() => {
		isMountedRef.current = true;
		fetchSuggestions();

		return () => {
			isMountedRef.current = false;
			cleanup();
		};
	}, [fetchSuggestions, cleanup]);

	const retry = useCallback(() => {
		retryCountRef.current = 0;
		fetchSuggestions();
	}, [fetchSuggestions]);

	return {
		...state,
		retry,
	};
}
